'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  Loader2,
  Pause,
  Play,
  RotateCw,
  VideoOff,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { withPosterFragment } from '@kit/shared/video-poster';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import { resolveVideoSource } from '~/lib/media/video-source';
import { useMuxHls } from '../use-mux-hls';
import { DancerSection } from './dancer-section';
import type { DancerMediaItem } from './types';

interface Props {
  reel: DancerMediaItem;
}

// `useSyncExternalStore` callbacks for the `prefers-reduced-motion`
// media query. Defined at module scope so React can use referential
// equality across renders. SSR returns `false` (i.e. motion is OK) so
// the static-fallback branch doesn't flash during hydration.
function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getReducedMotionSnapshot(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getReducedMotionServerSnapshot(): boolean {
  return false;
}

/**
 * Recovery shell around the player below.
 *
 * `useMuxHls.failed` is per-mount and cannot clear itself: the inner component
 * unmounts its <video> on failure, so the effect can never succeed again from
 * the same mount. Remounting is the only recovery, which is why this outer half
 * owns `attempt` and keys on it — the same shape as MuxHlsPlayer. Without it a
 * transient /api/media/mux-token 500 left this page, the public SEO-facing
 * dancer surface, showing a permanent "unavailable" card that only a full
 * reload could clear.
 *
 * `reel.id` is in the inner key so a media swap also remounts. Resetting
 * `attempt` across a swap is the job of `key={reel.id}` on THIS component, at
 * its mount site in PublicDancerView.
 */
export function DancerReel({ reel }: Props) {
  const [attempt, setAttempt] = useState(0);
  return (
    <DancerReelInner
      key={`${reel.id}:${attempt}`}
      reel={reel}
      onRetry={() => setAttempt((a) => a + 1)}
    />
  );
}

/**
 * Lead reel — inline 16:9 video player that auto-plays muted when
 * scrolled into view, pauses when out of view. Click to toggle mute;
 * dedicated mute button bottom-right; spacebar / click on the surface
 * toggles play/pause.
 *
 * Why a dedicated component (vs reusing `<DancerVideos>`):
 *  - Different rendering: inline `<video>` element, not a click-to-open
 *    tile.
 *  - Different interaction model: auto-play on intersection.
 *  - Different layout: single full-width 16:9 player, not a grid tile.
 *  - The proof-first ordering (Phili 2026-05-11 audio brief) puts the
 *    reel at the top of the body; "can they move?" should be answered
 *    in the first 15 seconds, with zero clicks required.
 *
 * Accessibility:
 *  - Respects `prefers-reduced-motion: reduce` — falls back to a static
 *    click-to-play surface mirroring the `<DancerVideos>` tile look.
 *  - Mute toggle is a real button with `aria-pressed`.
 *  - The video element has `aria-label` derived from filename.
 *
 * Constraints:
 *  - Only handles uploaded videos (signed URL / storage_path). External
 *    URLs (YouTube / Vimeo) need iframe embeds, which is out of scope
 *    for this component; the lead-reel selector in `<PublicDancerView>`
 *    already filters `!external_url`.
 */
function DancerReelInner({ reel, onRetry }: Props & { onRetry: () => void }) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  // A native <video> whose source will never decode. The corrupt-originals
  // class (BUG_INVESTIGATION_dancer_reel_corrupt_r2_originals_2026_09_18) hands
  // `<video src>` an R2 object with a zero-byte prefix, so the element settles
  // at MEDIA_ERR_SRC_NOT_SUPPORTED and every control below is dead. Without
  // this the viewer gets a play button that can never start anything, on a
  // public, SEO-indexed page. The Mux branch already says "unavailable"; this
  // gives the native branch the same voice.
  const [nativeFailed, setNativeFailed] = useState(false);

  // Detect reduced-motion via useSyncExternalStore so we (a) read the
  // value synchronously on first render rather than firing a cascading
  // setState inside an effect, and (b) get SSR-safe hydration via the
  // explicit server snapshot. The previous effect-based pattern tripped
  // the "no setState inside effect" lint rule.
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );

  // BAL-721: a ready Mux asset streams browser-safe signed HLS; the hook
  // attaches it to this same <video> so all the auto-play/mute/reduced-motion
  // logic below is unchanged. A still-encoding asset shows a processing state
  // (R4); anything else falls back to the native signed URL (R3).
  //
  // Resolved BEFORE the observer effect so `muxFailed` can be one of its
  // dependencies — see the effect below. `resolveVideoSource` is a pure
  // function of props, so hoisting it changes nothing but ordering.
  const renderSource = resolveVideoSource({
    uploadedUrl: reel.signed_url,
    muxPlaybackId: reel.mux_playback_id,
    muxStatus: reel.mux_status,
  });
  const isMux = renderSource.kind === 'mux';
  const isProcessing = renderSource.kind === 'processing';
  // `failed` was discarded here until 2026-08-28. When the token fetch or the
  // HLS attach fails the element keeps no playable source, so `play()` rejects,
  // `needsTap` goes true — and the tap CTA IS the play button. The viewer got a
  // play button that did nothing, forever. Render the unavailable state instead.
  const { failed: muxFailed } = useMuxHls(videoRef, isMux ? reel.id : null);

  // IntersectionObserver: play when ≥50% of the player is visible;
  // pause when it leaves. Skip when reduced-motion is on.
  //
  // `muxFailed` / `nativeFailed` are dependencies, not decoration: on failure
  // this component returns the "unavailable" card and the <video> unmounts. Without it in the
  // deps the effect never re-runs, so the observer is never disconnected and
  // keeps the detached container and video alive until the component unmounts.
  // A detached node reports ratio 0, so the callback calls pause() on an
  // orphan — wasted work on a dead element, not a resurrected player. Pinned by
  // D4 in __tests__/components/public-dancer-view/dancer-reel.test.tsx.
  useEffect(() => {
    if (reducedMotion || muxFailed || nativeFailed) return;
    const node = containerRef.current;
    const video = videoRef.current;
    if (!node || !video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.intersectionRatio >= 0.5) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            // Browsers can reject autoplay (e.g. Safari low-power mode).
            // Surface a tap CTA in that case instead of failing silently.
            playPromise.catch(() => setNeedsTap(true));
          }
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [reducedMotion, muxFailed, nativeFailed]);

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      // play() can reject with NotSupportedError when the <video> has no
      // playable source loaded (404, unsupported codec, readyState 0) or
      // with NotAllowedError when autoplay is blocked. Mirror the
      // IntersectionObserver pattern: surface the tap CTA on failure
      // instead of letting the rejection bubble up to Sentry.
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise
          .then(() => setNeedsTap(false))
          .catch(() => {
            setNeedsTap(true);
            setPlaying(false);
          });
      } else {
        setNeedsTap(false);
      }
    } else {
      try {
        video.pause();
      } catch {
        // pause() can throw if the element is in an unsupported state;
        // there's nothing actionable to do, the onPause handler will sync
        // playing state if the pause did succeed.
      }
    }
  };

  const handleToggleMute = (event: React.MouseEvent) => {
    event.stopPropagation();
    setMuted((prev) => !prev);
  };

  // Native mode sets a direct src; Mux mode leaves it unset (the hook attaches).
  const src = isMux ? undefined : (reel.signed_url ?? reel.storage_path ?? '');
  const label = reel.file_name ?? t('dancer:publicView.reel.defaultLabel');

  // Encode still running — show a processing surface, never a broken player (R4).
  if (isProcessing) {
    return (
      <DancerSection
        eyebrow={t('dancer:publicView.reel.eyebrow')}
        title={t('dancer:publicView.reel.title')}
      >
        <div className="bg-secondary border-foreground/15 relative grid aspect-video w-full place-items-center overflow-hidden border">
          <div className="flex flex-col items-center gap-3 text-white/80">
            <Loader2 className="size-8 animate-spin" />
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase">
              <Trans i18nKey="dancer:publicView.reel.processing" />
            </span>
          </div>
        </div>
      </DancerSection>
    );
  }

  // Playback is impossible — a Mux token fetch / HLS attach failed, or the
  // native element rejected its source. Say so, rather than showing a play
  // button that can never start anything (R4).
  if (muxFailed || nativeFailed) {
    return (
      <DancerSection
        eyebrow={t('dancer:publicView.reel.eyebrow')}
        title={t('dancer:publicView.reel.title')}
      >
        <div
          role="alert"
          className="bg-secondary border-foreground/15 relative grid aspect-video w-full place-items-center overflow-hidden border"
        >
          <div className="flex flex-col items-center gap-3 text-white/80">
            <VideoOff className="size-8" />
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase">
              <Trans i18nKey="dancer:publicView.reel.unavailable" />
            </span>
            {/* The failure is usually transient (a token 500, a network blip).
                Without this the card is terminal until a full page reload. */}
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RotateCw className="size-3.5" />
              <Trans i18nKey="common:retry" />
            </Button>
          </div>
        </div>
      </DancerSection>
    );
  }

  return (
    <DancerSection
      eyebrow={t('dancer:publicView.reel.eyebrow')}
      title={t('dancer:publicView.reel.title')}
    >
      <div
        ref={containerRef}
        className="bg-secondary border-foreground/15 group relative aspect-video w-full overflow-hidden border"
      >
        <video
          ref={videoRef}
          src={withPosterFragment(src)}
          aria-label={label}
          muted={muted}
          loop
          playsInline
          // Browsers ignore the attribute when reduced-motion is honoured by
          // the IntersectionObserver branch above; setting it here lets
          // browsers that don't expose the media query still respect intent.
          autoPlay={!reducedMotion}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          // Only the native path: in Mux mode the hook owns the source and
          // reports its own failure through `muxFailed`.
          onError={() => {
            if (!isMux) setNativeFailed(true);
          }}
          onClick={handleTogglePlay}
          className="h-full w-full object-cover"
        />

        {/* Tap-to-play overlay — shown when the browser rejected autoplay
            (e.g. iOS Safari with low-power mode) OR when reduced-motion
            disables autoplay entirely. Click anywhere on the player to
            dismiss and play. */}
        {(needsTap || reducedMotion) && !playing && (
          // eslint-disable-next-line kit-components/use-kit-primitives -- full-bleed `absolute inset-0` play overlay; kit Button's forced h-9 size overrides the inset fill and collapses the overlay to a 36px strip
          <button
            type="button"
            onClick={handleTogglePlay}
            aria-label={t('dancer:publicView.reel.playReel')}
            className="absolute inset-0 grid place-items-center bg-black/30 transition-colors hover:bg-black/40"
          >
            <span className="ring-foreground/30 grid size-16 place-items-center rounded-md bg-white/15 text-white ring-1 backdrop-blur-sm">
              <Play className="ml-0.5 size-7 fill-current" />
            </span>
          </button>
        )}

        {/* Mute toggle — bottom-right, always present. Stops propagation
            so the click doesn't also toggle play/pause. */}
        <Button
          type="button"
          variant="ghost"
          onClick={handleToggleMute}
          aria-pressed={muted}
          aria-label={
            muted
              ? t('dancer:publicView.reel.unmuteReel')
              : t('dancer:publicView.reel.muteReel')
          }
          className="ring-foreground/20 absolute right-4 bottom-4 z-10 inline-flex items-center gap-2 rounded-md bg-black/55 px-3 py-2 font-mono text-[10px] tracking-[0.22em] text-white uppercase ring-1 backdrop-blur-sm transition-colors hover:bg-black/70"
        >
          {muted ? (
            <>
              <VolumeX className="size-3.5" />
              <Trans i18nKey="dancer:publicView.reel.unmute" />
            </>
          ) : (
            <>
              <Volume2 className="size-3.5" />
              <Trans i18nKey="dancer:publicView.reel.mute" />
            </>
          )}
        </Button>

        {/* Subtle play/pause indicator — top-left, fades when scrubbing.
            Provides a visual confirmation that auto-play happened. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-4 left-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-white/85 uppercase opacity-0 transition-opacity group-hover:opacity-100"
        >
          {playing ? (
            <>
              <Pause className="size-3" />
              <Trans i18nKey="dancer:publicView.reel.playing" />
            </>
          ) : (
            <>
              <Play className="size-3" />
              <Trans i18nKey="dancer:publicView.reel.paused" />
            </>
          )}
        </div>
      </div>
    </DancerSection>
  );
}
